import React, { useEffect, useCallback, useRef } from 'react';
import '../styles/video-dialog.css';

const VideoDialog = ({ isOpen, onClose, videoUrl }) => {
    const videoRef = useRef(null);
    const playPromiseRef = useRef(null);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    // Clean up function for video
    const cleanupVideo = useCallback(() => {
        if (videoRef.current) {
            // If there's a pending play promise, wait for it to resolve
            if (playPromiseRef.current) {
                playPromiseRef.current
                    .then(() => {
                        if (videoRef.current) {
                            videoRef.current.pause();
                            videoRef.current.currentTime = 0;
                        }
                    })
                    .catch(() => {
                        // Play was already interrupted, nothing to do
                    });
                playPromiseRef.current = null;
            } else {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';

            // Small delay to ensure video element is fully mounted
            const timer = setTimeout(() => {
                if (videoRef.current && isOpen) {
                    // Store the play promise
                    playPromiseRef.current = videoRef.current.play();

                    playPromiseRef.current
                        .then(() => {
                            // Video started playing successfully
                            playPromiseRef.current = null;
                        })
                        .catch(error => {
                            playPromiseRef.current = null;
                            if (error.name === 'AbortError') {
                                console.log('Video play was aborted - this is normal when closing quickly');
                            } else if (error.name === 'NotAllowedError') {
                                console.log('Auto-play was prevented by browser policy');
                            } else {
                                console.log('Video play error:', error);
                            }
                        });
                }
            }, 100);

            return () => {
                clearTimeout(timer);
            };
        } else {
            cleanupVideo();
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, handleKeyDown, cleanupVideo]);

    // Cleanup when component unmounts
    useEffect(() => {
        return () => {
            cleanupVideo();
        };
    }, [cleanupVideo]);

    if (!isOpen) return null;

    function logVideoError(e) {
        const videoElement = e.target; // Get the video element that triggered the event
        console.error('🚨 Video Error Event:', e);

        if (videoElement.error) {
            console.error('💥 MediaError Code:', videoElement.error.code);
            console.error('💥 MediaError Message:', videoElement.error.message);

            // Decode the error code (this is the most important part)
            const errorMap = {
                1: 'MEDIA_ERR_ABORTED - User aborted playback.',
                2: 'MEDIA_ERR_NETWORK - A network error caused playback to fail.',
                3: 'MEDIA_ERR_DECODE - The video was corrupt or used an unsupported codec.',
                4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - The video source is not supported.'
            };
            console.error('🔍 Interpretation:', errorMap[videoElement.error.code] || 'Unknown error');
        }

        // Also log the current source of the video
        console.error('📹 Current video source:', videoElement.currentSrc);
    }

    return (
        <div className="video-dialog-overlay" onClick={handleBackdropClick}>
            <div className="video-dialog">
                <button className="video-dialog-close" onClick={onClose}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                <div className="video-container">
                    <video
                        ref={videoRef}
                        controls
                        muted
                        playsInline
                        className="demo-video"
                        preload="metadata"
                        onLoadStart={() => console.log('Video loading started')}
                        onCanPlay={() => console.log('Video can play')}
                        onError={(e) => console.error('Video error:', logVideoError(e))}
                    >
                        <source src={videoUrl} type="video/mp4" />
                        <source src={videoUrl.replace('.mp4', '.webm')} type="video/webm" />
                        Your browser does not support the video tag.
                    </video>
                </div>
            </div>
        </div>
    );
};

export default VideoDialog;